import { useState } from "react";
import * as api from "../api.js";
import { ICONES, Icone } from "../visual.jsx";
import { useAcoes, useEstado, usePode } from "../estado.jsx";
import { useNavegacao } from "../navegacao.jsx";
import { Botao, MenuDeAcoes, Rosto } from "./comuns.jsx";
import { confirmar, pedirTexto } from "./pergunta.tsx";

/*
 * A pele do gatilho de menu, igual à do `Botao` de contorno.
 *
 * `MenuDeAcoes` desenha o gatilho com o que o chamador manda, e aqui ele tem de
 * ser irmão dos botões da mesma fileira — três aparências para cinco ações na
 * mesma linha é a barra de escrever de novo, em escala menor. Constante e não
 * copiada em cada um: são três menus.
 */
/* A mesma medida do `Botao` `normal` de tamanho `padrao` (36, 44 no dedo, raio
   10, 14/600): eram 12,5px e 34 de altura ao lado de Arquivar e Apagar, que já
   vinham do `Botao`. O afundar e o anel são da base, iguais para todo botão. */
const PELE = [
  "inline-flex min-h-padrao flex-none items-center gap-2 rounded-controle border border-borda-controle bg-card",
  "px-3.5 text-corpo font-semibold whitespace-nowrap text-tinta-media hover:bg-muted hover:text-foreground",
  "data-[popup-open]:bg-muted max-[759px]:min-h-toque",
].join(" ");

/** Quantas etiquetas do catálogo cabem no menu antes de ele virar rolagem. */
const ETIQUETAS_NO_MENU = 10;

/**
 * A barra da seleção da mesa — o que se FAZ com trinta atendimentos de uma vez.
 *
 * Existe porque a mesa é lista e o trabalho dela é repetitivo: o canal de
 * anúncio despeja vinte números errados numa tarde, a etiqueta que organiza a
 * semana nasce depois de os casos já existirem, e a carteira de quem saiu de
 * férias passa inteira para outra pessoa. Um a um, cada um desses gestos é abrir
 * o caso, achar o controle e confirmar — vinte vezes.
 *
 * Três decisões de desenho:
 *
 *   1. **Ela nasce ABAIXO do filtro, não em cima dele.** O gesto que a mesa
 *      pedia é "arquive todos os que este filtro está mostrando": esconder o
 *      filtro para mostrar a barra tiraria da tela justamente o que define o
 *      lote. Por isso também o "Todos" traz o recorte INTEIRO do servidor, e não
 *      as 200 linhas carregadas.
 *   2. **Ação com nome escrito, não fileira de ícones.** A barra das mensagens é
 *      de ícones porque mora sobre a conversa e tem seis ações conhecidas; aqui
 *      as ações são raras (ninguém arquiva em lote toda hora) e duas delas não
 *      voltam atrás. Ícone sozinho, no dedo, é adivinhação com consequência.
 *   3. **Etiqueta não pergunta; etapa, titular, arquivar e apagar perguntam.**
 *      Pôr rótulo é reversível num toque — e é o gesto que se repete. Mover
 *      etapa dispara a régua de cada caso, trocar titular muda a carga de duas
 *      pessoas, e as outras duas tiram o caso da mesa.
 */
export default function SelecaoDeCasos({ marcados, definir, sair }) {
  const { mesa, totalDaMesa, etapas, equipe, catalogoDeTags } = useEstado();
  const { emLote, avisar } = useAcoes();
  const { termo, filtro, etapaDaMesa, respDaMesa, tagDaMesa, grupoDaMesa, criativoDaMesa, numeroDaMesa } = useNavegacao();
  const pode = usePode();
  const [buscandoTodos, setBuscandoTodos] = useState(false);

  const n = marcados.length;
  const plural = n === 1 ? "atendimento" : "atendimentos";
  const todosMarcados = n > 0 && n >= totalDaMesa;

  /**
   * Roda a ação e conta o que aconteceu.
   *
   * A frase vem de quem chamou porque em lote ela depende do resultado: "30
   * arquivados" e "27 arquivados · 3 não deram — Angélica: já está arquivado"
   * são a mesma ação com desfechos diferentes, e um recado que só diz "pronto"
   * esconde os três que ficaram para trás.
   */
  const rodar = async (nome, fazer, frase, { saem = false, mantendoSelecao = false } = {}) => {
    const r = await emLote(nome, marcados, fazer, { saem });
    if (!r) return;
    const falha = r.falhas?.[0];
    avisar(falha ? `${frase(r.feitos)} · ${r.falhas.length} não deu — ${falha.nome}: ${falha.erro}` : `${frase(r.feitos)}.`);
    if (!mantendoSelecao) sair();
  };

  const marcarTodos = async () => {
    setBuscandoTodos(true);
    try {
      /* O MESMO recorte que a lista está mostrando — é isso que "todos"
         significa aqui. Sem ele o botão marcaria a mesa inteira enquanto a tela
         mostra sete casos filtrados. */
      const r = await api.idsDaMesa({
        q: termo,
        filtro,
        etapa: etapaDaMesa,
        resp: respDaMesa,
        tag: tagDaMesa,
        grupo: grupoDaMesa,
        criativo: criativoDaMesa,
        numero: numeroDaMesa,
      });
      definir(r.ids ?? []);
    } catch (e) {
      avisar(e.message);
    } finally {
      setBuscandoTodos(false);
    }
  };

  /* As etiquetas que os marcados JÁ têm — é o que se pode tirar em lote. Sai das
     linhas carregadas (a mesa manda `tags` em cada uma), então com o recorte
     inteiro marcado a lista cobre o que está na tela; pôr, que é o gesto comum,
     vale para todos de qualquer forma. */
  const daSelecao = new Set(marcados);
  const etiquetasMarcadas = [
    ...new Set(mesa.filter((c) => daSelecao.has(c.id)).flatMap((c) => (c.tags ? c.tags.split("|") : []))),
  ].slice(0, ETIQUETAS_NO_MENU);

  const porEtiqueta = (tag) =>
    rodar(
      "lote-etiqueta",
      (ids) => api.loteEtiqueta(ids, tag),
      (q) => `“${tag}” em ${q} ${q === 1 ? "caso" : "casos"}`,
      {
        mantendoSelecao: true,
      },
    );

  return (
    /* Seleção é TINTA, como a marca redonda na linha e o ✓ do cabeçalho: o
       verde daqui dizia "deu certo" sobre um estado que ainda não fez nada. O
       recuo é o 16 da coluna. */
    <div className="flex flex-col gap-2 border-b border-b-border bg-foreground/4 px-4 py-2.5">
      {/* Quantos, o "todos" e a saída. O número é o que confirma o que vai
          acontecer — e fica no começo da linha, onde o olho já está. */}
      <div className="flex items-center gap-2">
        <span className="flex min-w-0 items-center gap-1.5 text-corpo font-bold text-foreground tabular-nums">
          <Icone de={ICONES.certo} tamanho={16} />
          {n ? `${n} ${plural}` : "Toque nas conversas"}
        </span>
        <div className="min-w-0 flex-1" />
        {/* "Todos" é o recorte do servidor, não a página: com filtro ligado ele
            diz quantos são de verdade. Já marcado tudo, ele vira "Limpar" — o
            mesmo lugar para o gesto contrário. */}
        {totalDaMesa > 0 && (
          <Botao
            rotulo={buscandoTodos ? "marcando…" : todosMarcados ? "Limpar" : `Todos (${totalDaMesa})`}
            variante="fantasma"
            aoTocar={todosMarcados ? () => definir([]) : marcarTodos}
            desabilitado={buscandoTodos}
          />
        )}
        <button
          type="button"
          onClick={sair}
          aria-label="Sair da seleção"
          data-dica="Sair da seleção"
          className="alvo flex-none cursor-pointer rounded-etiqueta border-0 bg-transparent p-0 text-tinta-fraca hover:text-foreground"
        >
          <Icone de={ICONES.fechar} tamanho={16} />
        </button>
      </div>

      {/* As ações. Quebram em duas linhas na coluna estreita e no telefone —
          fileira que rola de lado esconde justamente a última, e a última aqui é
          "Apagar". */}
      {n > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          {pode("registros:editar") && (
            <MenuDeAcoes
              rotulo="Etiquetar os selecionados"
              titulo="Pôr (ou tirar) uma etiqueta nos selecionados"
              legenda="Etiqueta — a pasta do escritório"
              classe={PELE}
              largura="248px"
              altura="360px"
              itens={[
                ...catalogoDeTags.slice(0, ETIQUETAS_NO_MENU).map((c) => ({
                  chave: `por:${c.tag}`,
                  rotulo: c.tag,
                  detalhe: `já em ${c.n} caso(s)`,
                  icone: ICONES.pasta,
                  aoTocar: () => porEtiqueta(c.tag),
                })),
                {
                  chave: "nova",
                  rotulo: "Etiqueta nova…",
                  icone: ICONES.somar,
                  aoTocar: async () => {
                    const tag = await pedirTexto({
                      titulo: `Etiqueta para ${n} ${plural}`,
                      texto: "Ela nasce escrita — não há cadastro antes do uso. Vira filtro na mesa na hora.",
                      convite: "audiência marcada",
                      acao: "Etiquetar",
                    });
                    if (tag) porEtiqueta(tag);
                  },
                },
                ...etiquetasMarcadas.map((tag) => ({
                  chave: `tirar:${tag}`,
                  rotulo: `Tirar “${tag}”`,
                  icone: ICONES.fechar,
                  perigo: true,
                  aoTocar: () =>
                    rodar(
                      "lote-etiqueta",
                      (ids) => api.loteEtiqueta(ids, tag, true),
                      (q) => `“${tag}” saiu de ${q} ${q === 1 ? "caso" : "casos"}`,
                      { mantendoSelecao: true },
                    ),
                })),
              ]}
            >
              <Icone de={ICONES.pasta} tamanho={16} />
              Etiqueta
            </MenuDeAcoes>
          )}

          {pode("registros:mover") && (
            <MenuDeAcoes
              rotulo="Mover os selecionados de etapa"
              titulo="Mover os selecionados para outra etapa do funil"
              legenda="Mover para"
              classe={PELE}
              largura="248px"
              altura="360px"
              itens={(etapas ?? []).map((e) => ({
                chave: e.chave,
                rotulo: e.rotulo,
                icone: ICONES.matriz,
                aoTocar: async () => {
                  /* Mover etapa dispara régua e cobrança em CADA caso — é a
                     linha da automação: o que sai da casa espera gente. */
                  const ok = await confirmar({
                    titulo: `Mover ${n} ${plural} para “${e.rotulo}”?`,
                    texto:
                      "Cada caso anda no funil e dispara a régua da etapa nova — inclusive a cobrança de documento, se ela existir lá.",
                    acao: "Mover",
                  });
                  if (!ok) return;
                  rodar(
                    "lote-etapa",
                    (ids) => api.loteEtapa(ids, e.chave),
                    (q) => `${q} movido(s) para “${e.rotulo}”`,
                  );
                },
              }))}
            >
              <Icone de={ICONES.matriz} tamanho={16} />
              Etapa
            </MenuDeAcoes>
          )}

          {pode("registros:editar") && (equipe ?? []).length > 0 && (
            <MenuDeAcoes
              rotulo="Trocar quem cuida dos selecionados"
              titulo="Passar os selecionados para outra pessoa"
              legenda="Passa a cuidar"
              classe={PELE}
              largura="248px"
              altura="360px"
              itens={equipe.map((p) => ({
                chave: p.nome,
                rotulo: p.nome,
                /* A foto do cadastro, a mesma que a linha da mesa mostra. Sem
                   ela eram cinco bonequinhos iguais para cinco pessoas. */
                rosto: <Rosto pessoa={p} tamanho={28} />,
                aoTocar: async () => {
                  const ok = await confirmar({
                    titulo: `Passar ${n} ${plural} para ${p.nome}?`,
                    texto:
                      "Quem cuidava sai do caso, e a carga dos dois muda na hora. As pendências passam a tocar o sino de quem recebeu.",
                    acao: "Transferir",
                  });
                  if (!ok) return;
                  rodar(
                    "lote-responsaveis",
                    (ids) => api.loteResponsaveis(ids, [p.nome]),
                    (q) => `${q} ${q === 1 ? "atendimento" : "atendimentos"} com ${p.nome}`,
                  );
                },
              }))}
            >
              <Icone de={ICONES.equipe} tamanho={16} />
              Quem cuida
            </MenuDeAcoes>
          )}

          {pode("registros:arquivar") && (
            <Botao
              icone={ICONES.arquivar}
              rotulo="Arquivar"
              titulo="Tirar os selecionados da mesa — dá para trazer de volta"
              aoTocar={async () => {
                const ok = await confirmar({
                  titulo: `Arquivar ${n} ${plural}?`,
                  texto:
                    "Saem da mesa e param de contar nas pastilhas. A conversa, os arquivos e a trilha ficam guardados — dá para trazer de volta.",
                  acao: "Arquivar",
                });
                if (!ok) return;
                rodar("lote-arquivar", api.loteArquivar, (q) => `${q} arquivado(s)`, { saem: true });
              }}
            />
          )}

          {pode("registros:apagar") && (
            <Botao
              icone={ICONES.apagar}
              rotulo="Apagar"
              titulo="Apagar os selecionados de vez"
              /* Destrutivo, e não terra: terra é o que vai ao cliente e o que
                 pede atenção; apagar de vez é a cor do estrago. */
              variante="perigo"
              aoTocar={async () => {
                const ok = await confirmar({
                  titulo: `Apagar ${n} ${plural} de vez?`,
                  /* A mesma frase do gesto de um caso, no plural: prometer menos
                     do que se apaga é o pior jeito de falhar. */
                  texto:
                    "Some TUDO de cada um: a ficha, a conversa, os prazos, a trilha, as peças da esteira, os contratos assinados, as gravações e a pasta de documentos no disco. Não dá para desfazer — para tirar da mesa sem perder nada, use Arquivar. Fica registrado quem apagou.",
                  acao: `Apagar ${n} de vez`,
                  perigo: true,
                });
                if (!ok) return;
                rodar("lote-apagar", api.loteApagar, (q) => `${q} apagado(s)`, { saem: true });
              }}
            />
          )}
        </div>
      )}
    </div>
  );
}
